import { useId } from 'react';

export interface RegisterRowProps {
  /**
   * One character per cell: `1` is lit, anything else is unlit. The default is
   * a fixed pattern rather than a random one, so the server HTML and the
   * client's agree and the row never changes between renders.
   */
  bits?: string | undefined;
  /** Index of the one cell that carries Amber. Omit for a row with no accent. */
  carry?: number | undefined;
  className?: string | undefined;
}

const DEFAULT_BITS = '1011000110101101';

/** Cell edge and pitch, in the identity's unit grid: a 1u cell on a 2u pitch. */
const CELL = 10;
const PITCH = 20;

/**
 * The register row: a line of small cells, a few of them lit — the identity's
 * one sanctioned decorative element, used as a divider or a footer.
 *
 * It is structure shown louder, not ornament: cells sit on the unit grid, the
 * radius is a fifth of a side, and there is at most one carry. It is flat vector
 * and never fades — a row is thinned by dropping cells, not with opacity or a
 * gradient. Decorative, so hidden from assistive technology.
 *
 * Lit cells take the text colour and unlit ones the hairline colour, so the row
 * re-inks itself on an Ink band with nothing to override.
 */
export function RegisterRow({ bits = DEFAULT_BITS, carry, className }: RegisterRowProps) {
  const id = useId();
  const cells = [...bits];
  const width = cells.length * PITCH - (PITCH - CELL);

  return (
    <svg
      viewBox={`0 0 ${width} ${CELL}`}
      className={className}
      aria-hidden="true"
      focusable="false"
      data-register={id}
    >
      {cells.map((bit, index) => (
        <rect
          // The row is fixed-length and never reordered, so the index is stable.
          key={index}
          x={index * PITCH}
          width={CELL}
          height={CELL}
          rx={CELL / 5}
          fill={
            index === carry
              ? 'var(--hs-color-carry)'
              : bit === '1'
                ? 'var(--hs-color-text)'
                : 'var(--hs-color-border)'
          }
        />
      ))}
    </svg>
  );
}
